/**
 * Advisor Today Service
 *
 * API functions for Today page data and widget preferences.
 */

import { API_CONFIG } from '../config/api'
import { handleAuthError } from '../utils/authErrorHandler'

function checkAuth(res: Response) {
    if (res.status === 401) {
        handleAuthError()
        throw new Error('Unauthorized')
    }
}

async function getHeaders(getToken: () => Promise<string>): Promise<HeadersInit> {
    const token = await getToken()
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    }
}

// Widget preference types
export interface WidgetPreference {
    widget_key: string
    visible: boolean
    position: number
}

// Today page data types
export interface MetricSolution {
    id: string
    account_id: string
    account_name: string
    subcategory_label: string
    total_suppliers: number
}

export interface MetricAccount {
    id: string
    name: string
    industry: string | null
    solution_count: number
}

export interface TodayMetrics {
    active_count: number
    qualifying_count: number
    account_count: number
    discovered_count: number
    pipeline_value_monthly: number
    active_solutions: MetricSolution[]
    qualifying_solutions: MetricSolution[]
    accounts_detail: MetricAccount[]
    solutions_by_account: MetricAccount[]
}

export interface NeedsActionItem {
    severity: 'danger' | 'warning' | 'info'
    sort_key: number
    text: string
    account_name: string
    account_id: string
    action: string
    dollar_impact?: string | null
}

export interface PipelineHealthEntry {
    account_id: string
    account_name: string
    industry: string | null
    health: 'GREEN' | 'YELLOW' | 'RED'
    deal_count: number
    active_solutions: number
    days_since_last_touch: number
    next_action: { title: string; due_date: string | null } | null
}

export interface ProductMixEntry {
    category: string
    count: number
    total_mrr: number
    pct_of_total: number
    active: number
    qualifying: number
}

export interface DiscoveryCount {
    total_unmapped: number
    top_picks: Array<{
        account_id: string
        account_name: string
        subcategory: string
        confidence: number | null
    }>
}

export interface WinLoss {
    quarter: string
    won: number
    lost: number
    stalled: number
    close_rate: number | null
    top_loss_reasons: string[]
}

export interface RenewalEntry {
    account_id: string
    account_name: string
    solution: string
    vendor: string | null
    expires: string
    days_until: number
    monthly_cost: number | null
    status: string
}

export interface RecentAccount {
    id: string
    name: string
    industry: string | null
    employee_count_range: string | null
    updated_at: string
    last_activity_at: string | null
    domain: string | null
    logo_url: string | null
}

export interface TodaysMeetings {
    scheduled: Array<{
        id: string
        type: string
        time: string
        status: string
        account_id: string | null
        account_name: string | null
        account_logo: string | null
    }>
    activities: Array<{
        id: string
        title: string
        time: string
        participants: string[]
        account_id: string | null
        account_name: string | null
        account_logo: string | null
    }>
}

export interface Commission {
    ytd: number
    goal: number
    pct_to_goal: number
    closing_this_month: number
    next_30_days: number
    next_90_days: number
}

export interface ActivePromo {
    id: string
    name: string
    headline: string | null
    description: string | null
    type: string | null
    value_type: string | null
    value_amount: number | null
    deadline: string | null
    new_logo_only: boolean
    supplier_id: string
    supplier_name: string
}

export interface SupplierConcentration {
    suppliers: Array<{
        supplier_id: string
        supplier_name: string
        deal_count: number
        pct: number
    }>
    risk_note: string | null
}

export interface TodayData {
    metrics: TodayMetrics
    needs_action?: NeedsActionItem[]
    recent_accounts?: RecentAccount[]
    pipeline_health?: PipelineHealthEntry[]
    product_mix?: ProductMixEntry[]
    discovery_count?: DiscoveryCount
    win_loss?: WinLoss
    renewal_calendar?: RenewalEntry[]
    todays_meetings?: TodaysMeetings
    commission?: Commission
    active_promos?: ActivePromo[]
    supplier_concentration?: SupplierConcentration
}

// ============================
// Widget Preferences API
// ============================

export async function getWidgetPreferences(
    getToken: () => Promise<string>
): Promise<WidgetPreference[]> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/advisors/me/widget-preferences`, {
        headers: await getHeaders(getToken),
    })
    checkAuth(res)
    if (!res.ok) throw new Error('Failed to fetch widget preferences')
    const data = await res.json()
    return data.preferences
}

export async function updateWidgetPreferences(
    getToken: () => Promise<string>,
    preferences: Array<{ widget_key: string; visible: boolean; position?: number }>
): Promise<WidgetPreference[]> {
    const res = await fetch(`${API_CONFIG.baseURL}/api/advisors/me/widget-preferences`, {
        method: 'PUT',
        headers: await getHeaders(getToken),
        body: JSON.stringify({ preferences }),
    })
    checkAuth(res)
    if (!res.ok) throw new Error('Failed to update widget preferences')
    const data = await res.json()
    return data.preferences
}

// ============================
// Today Page Data API
// ============================

export async function getTodayData(
    getToken: () => Promise<string>,
    widgets?: string[]
): Promise<TodayData> {
    const params = widgets ? `?widgets=${widgets.join(',')}` : ''
    const res = await fetch(`${API_CONFIG.baseURL}/api/advisors/me/today${params}`, {
        headers: await getHeaders(getToken),
    })
    checkAuth(res)
    if (!res.ok) throw new Error('Failed to fetch today data')
    return res.json()
}
